import React, { useState } from 'react';

interface ChatAvatarProps {
  src?: string | null;
  name: string;
  size?: 'sm' | 'md' | 'lg';
}

/** Picks the first usable value out of a name fallback chain, skipping blank/"undefined"/"null" strings. */
export const pickDisplayName = (
  ...candidates: Array<string | null | undefined>
): string | undefined =>
  candidates.find((c) => {
    const trimmed = c?.trim().toLowerCase();
    return !!trimmed && trimmed !== 'undefined' && trimmed !== 'null';
  }) ?? undefined;

const getInitials = (name: string) => {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return trimmed.substring(0, 2).toUpperCase();
};

const getGradient = (name: string) => {
  const gradients = [
    'linear-gradient(135deg, #FF6B6B, #FF8E53)',
    'linear-gradient(135deg, #4facfe, #00f2fe)',
    'linear-gradient(135deg, #43e97b, #38f9d7)',
    'linear-gradient(135deg, #fa709a, #fee140)',
    'linear-gradient(135deg, #f83600, #f9d423)',
    'linear-gradient(135deg, #1784FF, #00D45A)',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length];
};

export const ChatAvatar: React.FC<ChatAvatarProps> = ({ src, name, size = 'md' }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
  };

  const showImage = src && !imageError;

  return (
    <div className={`relative flex-shrink-0 rounded-full ${sizeClasses[size]}`}>
      <div
        className="w-full h-full rounded-full overflow-hidden flex items-center justify-center font-bold text-white shadow-sm border border-[#2C2C2E]"
        style={{ background: !showImage ? getGradient(name) : '#1C1C1E' }}
      >
        {showImage && (
          <img
            src={src}
            alt={name}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            className={`w-full h-full rounded-full object-cover transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}
        {!showImage && <span>{getInitials(name)}</span>}
      </div>
    </div>
  );
};
